import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import {
  Receipt, CheckCircle, User, PhoneCall, Phone, Mail, FileText, FileSpreadsheet,
  AlertTriangle, Edit, Trash2, Eye, Plus, Calendar, Clock, Building, TrendingDown,
  RefreshCw, FileDown, Search, Settings, RotateCcw, Save, ChevronDown, ChevronRight,
  Users, XCircle, UserX, AlertOctagon, Pause, ArrowDownCircle, ArrowUpCircle,
  Lightbulb, Copy, Zap, Info, Link, ExternalLink, History, DollarSign, CreditCard
} from 'lucide-react';
import { DebouncedInput, DebouncedTextarea } from '../shared/DebouncedInputs';
import EnCobroConsolidado from './EnCobroConsolidado';
import PagadasConsolidado from './PagadasConsolidado';
import EnviarEstadoCuentaConsolidadoModal from '../modals/EnviarEstadoCuentaConsolidadoModal';
import NuevaGestionModal from '../modals/NuevaGestionModal';
import NuevoCompromisoModal from '../modals/NuevoCompromisoModal';

/**
 * Tab "Minutas de Cobranzas": 7 sub-tabs (En Cobro, Pagadas, Minutas, Estados de Cuenta,
 * Gestiones, Alertas, Comunicaciones). Recibe TODO el estado + handlers vía props para
 * preservar la fuente de verdad en el componente Finanzas padre.
 */
const CobranzaTab = (props) => {
  const [estadoCuentaConsolidadoOpen, setEstadoCuentaConsolidadoOpen] = React.useState(false);
  const [cuentaSeleccionadaParaEstado, setCuentaSeleccionadaParaEstado] = React.useState(null);
  const [gestionModalOpen, setGestionModalOpen] = React.useState(false);
  const [compromisoModalOpen, setCompromisoModalOpen] = React.useState(false);
  const [cuentaParaAccion, setCuentaParaAccion] = React.useState(null);
  const {
    // Estado
    cobranzasData, gestionesCobranza, alertasCobranza, minutasCobranza, clientesMorosos,
    proximaReunion, loadingCobranzas, loadingMinutas, busquedaCobranzas,
    estadoCuentaData, comunicacionesPanel, generandoAlertas, ejecutandoAutomatico,
    filtroGestionCliente, filtroGestionResultado, filtroGestionTipo, filtrosEstadoCuenta,
    loadingEstadoCuenta, loadingGestiones, loadingAlertas, loadingComunicaciones,
    compromisosCobranza,

    // Setters
    setBusquedaCobranzas, setAlertaSeleccionada, setClienteEnvioSeleccionado,
    setClienteGestionSeleccionado, setFiltroGestionCliente, setFiltroGestionResultado,
    setFiltroGestionTipo, setFiltrosEstadoCuenta, setNuevaGestion,
    setShowConfigComunicaciones, setShowCrearGestionDesdeAlerta, setShowEnviarEstadoCuenta,
    setShowEnviarRecordatorio, setShowHistorialAlertas, setShowNuevaGestion,
    setShowNuevoCompromiso,

    // Datos computados / catálogos
    canalesGestion, clientesConFacturas, clientesEstadoCuenta, cobranzasFiltradas,
    coloresPrioridad, estadosComunicacion, iconosAlerta, resultadosGestion,
    sugerenciasAccion, tiposComunicacion, tiposGestion,

    // Fetchers
    fetchAlertasCobranza, fetchCobranzasData, fetchCompromisosCobranza,
    fetchComunicacionesPanel, fetchConfigComunicaciones, fetchGestionesCobranza,
    fetchHistorialAlertas, fetchPlantillasCorreo,

    // Handlers de negocio
    handleAbrirHistorialFinanciero, handleAbrirNotaCredito, handleAbrirNotaDebito,
    handleAbrirRegistrarPago, handleActualizarAlerta, handleActualizarCompromiso,
    handleDeleteMinuta, handleEditMinuta, handleEjecutarAutomatico, handleGenerarAlertas,
    handleGenerarEstadoCuenta, handleOpenNewMinuta, handleVerHistorialGestiones,
    handleVerPDFEstadoCuenta, handleViewMinuta,

    // Utilidades
    formatCurrency, formatRutSinPuntos,
  } = props;

  return (
<div className='space-y-6'>
  {/* Sub-pestañas de Cobranza */}
  <Tabs defaultValue='en_cobro' className='w-full'>
    <div className='flex items-center justify-between mb-4'>
      <TabsList className='bg-amber-50 border border-amber-200 rounded-lg p-1 h-auto'>
        <TabsTrigger value='en_cobro' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-amber-500 data-[state=active]:text-white text-amber-800 hover:bg-amber-100 transition-colors'>
          <Receipt className='w-3.5 h-3.5' />
          En Cobro
          <Badge variant='outline' className='ml-1 bg-white/80 text-amber-700 border-amber-300 text-[11px] px-1.5 py-0'>
            {cobranzasData.totales?.cantidad_en_cobro || 0}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value='pagadas' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-amber-500 data-[state=active]:text-white text-amber-800 hover:bg-amber-100 transition-colors'>
          <CheckCircle className='w-3.5 h-3.5' />
          Pagadas
          <Badge variant='outline' className='ml-1 bg-white/80 text-amber-700 border-amber-300 text-[11px] px-1.5 py-0'>
            {cobranzasData.pagadas?.length || 0}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value='estados_cuenta' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-amber-500 data-[state=active]:text-white text-amber-800 hover:bg-amber-100 transition-colors'>
          <User className='w-3.5 h-3.5' />
          Estados de Cuenta
        </TabsTrigger>
        <TabsTrigger value='gestiones' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-amber-500 data-[state=active]:text-white text-amber-800 hover:bg-amber-100 transition-colors'>
          <PhoneCall className='w-3.5 h-3.5' />
          Gestiones
          <Badge variant='outline' className='ml-1 bg-white/80 text-amber-700 border-amber-300 text-[11px] px-1.5 py-0'>
            {gestionesCobranza?.length || 0}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value='alertas' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-red-500 data-[state=active]:text-white text-red-700 hover:bg-red-100 transition-colors'>
          <AlertTriangle className='w-3.5 h-3.5' />
          Alertas
          {alertasCobranza?.estadisticas?.criticas > 0 && (
            <Badge className='ml-1 bg-red-600 text-white border-red-700 text-[11px] px-1.5 py-0 animate-pulse'>
              {alertasCobranza.estadisticas.criticas}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value='comunicaciones' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-blue-500 data-[state=active]:text-white text-blue-700 hover:bg-blue-100 transition-colors'>
          <Mail className='w-3.5 h-3.5' />
          Comunicaciones
          {comunicacionesPanel?.estadisticas_hoy?.total > 0 && (
            <Badge variant='outline' className='ml-1 bg-white/80 text-blue-700 border-blue-300 text-[11px] px-1.5 py-0'>
              {comunicacionesPanel.estadisticas_hoy.total}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value='minutas' className='flex items-center gap-2 text-sm px-3 py-1.5 rounded-md data-[state=active]:bg-amber-500 data-[state=active]:text-white text-amber-800 hover:bg-amber-100 transition-colors'>
          <FileText className='w-3.5 h-3.5' />
          Minutas
          <Badge variant='outline' className='ml-1 bg-white/80 text-amber-700 border-amber-300 text-[11px] px-1.5 py-0'>
            {minutasCobranza?.length || 0}
          </Badge>
        </TabsTrigger>
      </TabsList>
      <div className='flex items-center gap-3'>
        <div className='relative'>
          <Input
            type='text'
            placeholder='Buscar factura, cliente o PSP...'
            value={busquedaCobranzas}
            onChange={(e) => setBusquedaCobranzas(e.target.value)}
            className='w-64 pl-8 h-9 text-sm'
          />
          <Search className='w-4 h-4 text-slate-400 absolute left-2.5 top-2.5' />
        </div>
        <Button 
          size='sm' 
          variant='outline'
          onClick={fetchCobranzasData}
          className='text-xs'
        >
          <RefreshCw className='w-3 h-3 mr-1' />
          Actualizar
        </Button>
      </div>
    </div>

    {/* Pestaña: Facturas en Cobro */}
    <TabsContent value='en_cobro' className='mt-0'>
      <EnCobroConsolidado
        onCrearGestion={(cuenta) => {
          setCuentaParaAccion(cuenta);
          setGestionModalOpen(true);
        }}
        onCrearCompromiso={(cuenta) => {
          setCuentaParaAccion(cuenta);
          setCompromisoModalOpen(true);
        }}
        onEnviarEstadoCuenta={(cuenta) => {
          setCuentaSeleccionadaParaEstado(cuenta);
          setEstadoCuentaConsolidadoOpen(true);
        }}
        onAbrirRegistrarPago={handleAbrirRegistrarPago}
        onAbrirNotaCredito={handleAbrirNotaCredito}
        onAbrirNotaDebito={handleAbrirNotaDebito}
      />
      <EnviarEstadoCuentaConsolidadoModal
        open={estadoCuentaConsolidadoOpen}
        onOpenChange={setEstadoCuentaConsolidadoOpen}
        cuenta={cuentaSeleccionadaParaEstado}
      />
      <NuevaGestionModal
        open={gestionModalOpen}
        onOpenChange={setGestionModalOpen}
        cuenta={cuentaParaAccion}
        onSuccess={() => fetchCobranzasData?.()}
      />
      <NuevoCompromisoModal
        open={compromisoModalOpen}
        onOpenChange={setCompromisoModalOpen}
        cuenta={cuentaParaAccion}
        onSuccess={() => { fetchCompromisosCobranza?.(); fetchCobranzasData?.(); }}
      />
    </TabsContent>

    {/* Pestaña: Facturas Pagadas */}
    <TabsContent value='pagadas' className='mt-0'>
      <PagadasConsolidado onVerHistorial={handleAbrirHistorialFinanciero} />
    </TabsContent>

    {/* Pestaña: Minutas de Reunión */}
    <TabsContent value='minutas' className='mt-0'>
      {/* Header con información de próxima reunión */}
      <div className='flex items-start justify-between mb-4'>
        <div className='flex items-center gap-4'>
          {proximaReunion && (
            <div className='bg-blue-50 border border-blue-200 rounded-lg px-4 py-2'>
              <p className='text-xs text-blue-600 font-medium'>Próxima Reunión</p>
              <p className='text-sm font-bold text-blue-800'>
                {proximaReunion.dia_semana} {new Date(proximaReunion.fecha + 'T12:00:00').toLocaleDateString('es-CL')} - {proximaReunion.hora}
              </p>
            </div>
          )}
          <Button 
            onClick={handleOpenNewMinuta}
            className='bg-primary hover:bg-primary/90'
            data-testid='nueva-minuta-btn'
          >
            <Plus className='w-4 h-4 mr-2' />
            Nueva Minuta
          </Button>
        </div>
      </div>

      {/* Aviso de comportamiento RUT-consolidado */}
      <Card className='p-3 bg-blue-50 border-blue-200 mb-3'>
        <div className='flex items-start gap-2 text-xs text-blue-800'>
          <AlertTriangle className='w-4 h-4 text-blue-600 shrink-0 mt-0.5' />
          <div>
            <p className='font-semibold'>Minutas por RUT consolidado</p>
            <p>
              Cada ítem se guarda con su <code>rut_normalizado</code> y un <em>snapshot</em> del saldo consolidado.
              Si un ítem cambia de RUT en una edición, los compromisos derivados NO se reasignan ni se eliminan:
              se conservan asociados al RUT original y se genera una alerta de revisión manual. Un nuevo compromiso
              para el RUT actualizado debe crearse explícitamente. Eliminar una minuta tampoco borra sus compromisos.
            </p>
          </div>
        </div>
      </Card>

      {/* Resumen de morosos actuales */}
      {clientesMorosos.length > 0 && (
        <Card className='p-4 bg-amber-50 border-amber-200 mb-4'>
          <div className='flex items-center gap-3'>
            <AlertTriangle className='w-5 h-5 text-amber-600' />
            <div>
              <p className='font-medium text-amber-800'>
                {clientesMorosos.length} cliente{clientesMorosos.length !== 1 ? 's' : ''} con cuotas vencidas
              </p>
              <p className='text-sm text-amber-700'>
                Total adeudado: {formatCurrency(clientesMorosos.reduce((sum, c) => sum + c.monto_adeudado, 0))}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Lista de minutas */}
      {loadingMinutas ? (
        <Card className='p-12 text-center border-slate-200'>
          <p className='text-slate-600'>Cargando minutas...</p>
        </Card>
      ) : minutasCobranza.length === 0 ? (
        <Card className='p-12 text-center border-slate-200'>
          <FileText className='w-16 h-16 text-slate-400 mx-auto mb-4' />
          <p className='text-slate-600 mb-4'>No hay minutas de cobranza registradas</p>
          <Button onClick={handleOpenNewMinuta} variant='outline'>
            <Plus className='w-4 h-4 mr-2' />
            Crear Primera Minuta
          </Button>
        </Card>
      ) : (
        <div className='space-y-4'>
          {minutasCobranza.map((minuta) => (
            <Card key={minuta.id} className='p-5 border-slate-200 hover:shadow-md transition-shadow'>
              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  <div className='flex items-center gap-3 mb-2'>
                    <Calendar className='w-5 h-5 text-primary' />
                    <span className='font-semibold text-slate-900'>
                      {new Date(minuta.fecha_reunion + 'T12:00:00').toLocaleDateString('es-CL', {
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                      })}
                    </span>
                    <Badge variant='outline' className='text-xs'>
                      {minuta.hora_reunion || '10:00'}
                    </Badge>
                  </div>
                  
                  <div className='flex items-center gap-4 text-sm text-slate-600 mb-3'>
                    <span className='flex items-center gap-1'>
                      <Users className='w-4 h-4' />
                      {minuta.asistentes_nombres?.length || 0} asistentes
                    </span>
                    <span className='flex items-center gap-1'>
                      <Building className='w-4 h-4' />
                      {minuta.items?.length || 0} clientes revisados
                    </span>
                  </div>

                  {minuta.observaciones_generales && (
                    <p className='text-sm text-slate-600 bg-slate-50 p-2 rounded'>
                      {minuta.observaciones_generales.substring(0, 150)}
                      {minuta.observaciones_generales.length > 150 ? '...' : ''}
                    </p>
                  )}

                  {(minuta.items || []).length > 0 && (
                    <details className='mt-2' data-testid={`minuta-items-toggle-${minuta.id}`}>
                      <summary className='text-xs text-blue-700 cursor-pointer'>Ver {minuta.items.length} ítem(s) con RUT y snapshot</summary>
                      <div className='mt-2 space-y-1'>
                        {minuta.items.map((it, i) => (
                          <div key={it.item_id || i} className='text-xs bg-slate-50 border border-slate-200 rounded p-2' data-testid={`minuta-item-${minuta.id}-${i}`}>
                            <p><strong>{it.client_name}</strong> · RUT: <span className='font-mono'>{it.rut_normalizado || it.client_rut || '(sin RUT normalizado)'}</span></p>
                            {it.saldo_consolidado_snapshot && (
                              <p className='text-slate-600'>
                                Saldo consolidado (snapshot): ${new Intl.NumberFormat('es-CL').format(it.saldo_consolidado_snapshot.saldo_total || 0)}
                                {' · '}#{it.saldo_consolidado_snapshot.n_facturas_pendientes || 0} facturas
                                {' · '}mora {it.saldo_consolidado_snapshot.dias_mora_mas_antigua || 0} días
                              </p>
                            )}
                            {it.factura_id && <p className='text-slate-500'>Factura ref: {it.factura_id.slice(0, 8)}…</p>}
                            {(it.compromisos_generados || []).length > 0 && (
                              <p className='text-purple-700'>Compromisos derivados: {it.compromisos_generados.length}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
                
                <div className='flex items-center gap-2'>
                  <Button 
                    variant='ghost' 
                    size='sm'
                    onClick={() => handleViewMinuta(minuta)}
                    data-testid={`view-minuta-${minuta.id}`}
                  >
                    <Eye className='w-4 h-4' />
                  </Button>
                  <Button 
                    variant='ghost' 
                    size='sm'
                    onClick={() => handleEditMinuta(minuta)}
                    data-testid={`edit-minuta-${minuta.id}`}
                  >
                    <Edit className='w-4 h-4' />
                  </Button>
                  <Button 
                    variant='ghost' 
                    size='sm'
                    onClick={() => handleDeleteMinuta(minuta.id)}
                    className='text-red-500 hover:text-red-700'
                    data-testid={`delete-minuta-${minuta.id}`}
                  >
                    <Trash2 className='w-4 h-4' />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </TabsContent>

    {/* Pestaña: Estados de Cuenta */}
    <TabsContent value='estados_cuenta' className='mt-0'>
      <div className='space-y-4'>
        {/* Filtros */}
        <Card className='p-4 bg-slate-50 border-slate-200'>
          <div className='grid grid-cols-1 md:grid-cols-5 gap-4 items-end'>
            {/* Selector de Cliente */}
            <div className='md:col-span-2'>
              <label className='block text-xs font-medium text-slate-700 mb-1'>Cliente</label>
              <select
                className='w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary'
                value={filtrosEstadoCuenta.client_id}
                onChange={(e) => setFiltrosEstadoCuenta(prev => ({ ...prev, client_id: e.target.value }))}
                data-testid='select-cliente-estado-cuenta'
              >
                <option value=''>Seleccione un cliente...</option>
                {clientesEstadoCuenta.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.empresa || cliente.nombre} {cliente.rut ? `(${cliente.rut})` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Fecha Desde */}
            <div>
              <label className='block text-xs font-medium text-slate-700 mb-1'>Desde</label>
              <input
                type='date'
                className='w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary'
                value={filtrosEstadoCuenta.fecha_desde}
                onChange={(e) => setFiltrosEstadoCuenta(prev => ({ ...prev, fecha_desde: e.target.value }))}
                data-testid='fecha-desde-estado-cuenta'
              />
            </div>
            
            {/* Fecha Hasta */}
            <div>
              <label className='block text-xs font-medium text-slate-700 mb-1'>Hasta</label>
              <input
                type='date'
                className='w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary'
                value={filtrosEstadoCuenta.fecha_hasta}
                onChange={(e) => setFiltrosEstadoCuenta(prev => ({ ...prev, fecha_hasta: e.target.value }))}
                data-testid='fecha-hasta-estado-cuenta'
              />
            </div>
            
            {/* Botones Generar y PDF */}
            <div className='flex gap-2'>
              <Button
                onClick={handleGenerarEstadoCuenta}
                disabled={loadingEstadoCuenta || !filtrosEstadoCuenta.client_id}
                className='flex-1 h-9 text-sm'
                data-testid='btn-generar-estado-cuenta'
              >
                {loadingEstadoCuenta ? (
                  <div className='animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2' />
                ) : (
                  <Eye className='w-4 h-4 mr-2' />
                )}
                Ver
              </Button>
              <Button
                onClick={handleVerPDFEstadoCuenta}
                disabled={!filtrosEstadoCuenta.client_id}
                variant='outline'
                className='h-9 text-sm border-primary text-primary hover:bg-primary hover:text-white'
                data-testid='btn-pdf-estado-cuenta'
              >
                <FileText className='w-4 h-4 mr-1' />
                PDF
              </Button>
            </div>
          </div>
          
          {/* Filtro de Estado */}
          <div className='mt-3 flex flex-wrap gap-2'>
            <span className='text-xs text-slate-500 self-center'>Filtrar:</span>
            {[
              { value: 'todos', label: 'Todos' },
              { value: 'con_saldo', label: 'Con Saldo' },
              { value: 'al_dia', label: 'Al Día' },
              { value: 'solo_vencidos', label: 'Vencidos' },
              { value: 'atraso_30_dias', label: '+30 días' },
              { value: 'atraso_60_dias', label: '+60 días' }
            ].map((filtro) => (
              <button
                key={filtro.value}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  filtrosEstadoCuenta.estado_filtro === filtro.value
                    ? filtro.value.includes('60') ? 'bg-red-600 text-white' :
                      filtro.value.includes('30') ? 'bg-orange-500 text-white' :
                      filtro.value === 'solo_vencidos' ? 'bg-amber-500 text-white' :
                      'bg-primary text-white'
                    : 'bg-white text-slate-600 border border-slate-300 hover:bg-slate-50'
                }`}
                onClick={() => setFiltrosEstadoCuenta(prev => ({ ...prev, estado_filtro: filtro.value }))}
              >
                {filtro.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Vista vacía */}
        {!estadoCuentaData && !loadingEstadoCuenta && (
          <Card className='p-8 text-center border-slate-200 border-dashed'>
            <FileText className='w-12 h-12 text-slate-300 mx-auto mb-3' />
            <h3 className='text-lg font-semibold text-slate-600 mb-1'>Sin Estado de Cuenta</h3>
            <p className='text-sm text-slate-500 max-w-md mx-auto'>
              Seleccione un cliente y período, luego presione "Generar" para ver el estado de cuenta.
            </p>
          </Card>
        )}

        {/* Loading */}
        {loadingEstadoCuenta && (
          <Card className='p-8 text-center'>
            <div className='animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full mx-auto mb-3' />
            <p className='text-slate-600'>Generando estado de cuenta...</p>
          </Card>
        )}

        {/* Estado de Cuenta Generado */}
        {estadoCuentaData && !loadingEstadoCuenta && (
          <div className='space-y-4'>
            {/* Alertas de Cobranza */}
            {(estadoCuentaData.alertas_cobranza?.length || 0) > 0 && (
              <div className='space-y-2'>
                {estadoCuentaData.alertas_cobranza.map((alerta, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border flex items-start gap-3 ${
                      alerta.severidad === 'critical' ? 'bg-red-50 border-red-300 text-red-800' :
                      alerta.severidad === 'high' ? 'bg-orange-50 border-orange-300 text-orange-800' :
                      'bg-amber-50 border-amber-300 text-amber-800'
                    }`}
                  >
                    <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      alerta.severidad === 'critical' ? 'text-red-600' :
                      alerta.severidad === 'high' ? 'text-orange-600' :
                      'text-amber-600'
                    }`} />
                    <div>
                      <p className='font-semibold text-sm'>{alerta.titulo}</p>
                      <p className='text-xs'>{alerta.mensaje}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cabecera del Cliente con Indicador de Riesgo */}
            <Card className='p-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white'>
              <div className='flex justify-between items-start'>
                <div className='flex items-start gap-3'>
                  {/* Indicador de Riesgo */}
                  {estadoCuentaData.indicador_riesgo && (
                    <div className={`w-3 h-full rounded-full flex-shrink-0 ${
                      estadoCuentaData.indicador_riesgo.nivel === 'verde' ? 'bg-green-500' :
                      estadoCuentaData.indicador_riesgo.nivel === 'amarillo' ? 'bg-yellow-500' :
                      estadoCuentaData.indicador_riesgo.nivel === 'naranja' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`} style={{minHeight: '60px'}} />
                  )}
                  <div>
                    <div className='flex items-center gap-2'>
                      <h3 className='text-lg font-bold'>
                        {estadoCuentaData.cliente?.empresa || estadoCuentaData.cliente?.nombre}
                      </h3>
                      {estadoCuentaData.indicador_riesgo && (
                        <Badge className={`text-[10px] ${
                          estadoCuentaData.indicador_riesgo.nivel === 'verde' ? 'bg-green-500' :
                          estadoCuentaData.indicador_riesgo.nivel === 'amarillo' ? 'bg-yellow-500 text-black' :
                          estadoCuentaData.indicador_riesgo.nivel === 'naranja' ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}>
                          {estadoCuentaData.indicador_riesgo.descripcion}
                        </Badge>
                      )}
                    </div>
                    <p className='text-slate-300 text-sm'>
                      RUT: {estadoCuentaData.cliente?.rut || 'N/A'}
                    </p>
                    {estadoCuentaData.cliente?.email && (
                      <p className='text-slate-300 text-xs'>
                        {estadoCuentaData.cliente.email}
                      </p>
                    )}
                  </div>
                </div>
                <div className='text-right'>
                  <p className='text-xs text-slate-300'>Estado de Cuenta</p>
                  <p className='text-sm font-semibold'>
                    {new Date(estadoCuentaData.periodo?.fecha_desde).toLocaleDateString('es-CL')} - {new Date(estadoCuentaData.periodo?.fecha_hasta).toLocaleDateString('es-CL')}
                  </p>
                  <p className='text-xs text-slate-400 mt-1'>
                    Emitido: {new Date(estadoCuentaData.periodo?.fecha_emision).toLocaleDateString('es-CL')}
                  </p>
                </div>
              </div>
            </Card>

            {/* Contratos Asociados */}
            {(estadoCuentaData.contratos_asociados?.length || 0) > 0 && (
              <Card className='border-slate-200 overflow-hidden'>
                <div className='p-3 bg-indigo-50 border-b border-indigo-200'>
                  <h4 className='text-sm font-semibold text-indigo-800 flex items-center gap-2'>
                    <Link className='w-4 h-4' />
                    Contratos Asociados ({estadoCuentaData.contratos_asociados.length})
                  </h4>
                </div>
                <div className='p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {estadoCuentaData.contratos_asociados.map((contrato, idx) => (
                    <div key={idx} className='bg-slate-50 rounded-lg p-3 border border-slate-200'>
                      <div className='flex items-center justify-between mb-1'>
                        <span className='font-mono text-xs font-semibold text-indigo-700'>{contrato.codigo}</span>
                        <Badge variant='outline' className='text-[10px] bg-green-50 text-green-700 border-green-200'>
                          {contrato.estado || 'Activo'}
                        </Badge>
                      </div>
                      <p className='text-xs text-slate-600 truncate' title={contrato.tipo_servicio}>
                        {contrato.tipo_servicio}
                      </p>
                      {contrato.monto_total && (
                        <p className='text-xs font-semibold text-slate-700 mt-1'>
                          {contrato.monto_total} {contrato.moneda || 'UF'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Tarjetas Resumen - Conceptos Separados */}
            <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
              {/* Saldo Inicial */}
              <Card className='p-3 bg-slate-50 border-slate-200'>
                <p className='text-[10px] text-slate-500 uppercase tracking-wide'>Saldo Inicial</p>
                <p className='text-lg font-bold text-slate-800 mt-1'>
                  ${(estadoCuentaData.resumen?.saldo_inicial || 0).toLocaleString('es-CL')}
                </p>
              </Card>
              
              {/* Facturado (separado de ND) */}
              <Card className='p-3 bg-red-50 border-red-200'>
                <p className='text-[10px] text-red-600 uppercase tracking-wide'>+ Facturado</p>
                <p className='text-lg font-bold text-red-700 mt-1'>
                  ${(estadoCuentaData.resumen?.total_facturado || 0).toLocaleString('es-CL')}
                </p>
              </Card>
              
              {/* Notas de Débito (separado) */}
              <Card className='p-3 bg-orange-50 border-orange-200'>
                <p className='text-[10px] text-orange-600 uppercase tracking-wide'>+ Notas Débito</p>
                <p className='text-lg font-bold text-orange-700 mt-1'>
                  ${(estadoCuentaData.resumen?.total_notas_debito || 0).toLocaleString('es-CL')}
                </p>
              </Card>
              
              {/* Pagos Recibidos (separado de NC) */}
              <Card className='p-3 bg-green-50 border-green-200'>
                <p className='text-[10px] text-green-600 uppercase tracking-wide'>- Pagos</p>
                <p className='text-lg font-bold text-green-700 mt-1'>
                  ${(estadoCuentaData.resumen?.total_pagos || 0).toLocaleString('es-CL')}
                </p>
              </Card>
              
              {/* Notas de Crédito (separado) */}
              <Card className='p-3 bg-blue-50 border-blue-200'>
                <p className='text-[10px] text-blue-600 uppercase tracking-wide'>- Notas Crédito</p>
                <p className='text-lg font-bold text-blue-700 mt-1'>
                  ${(estadoCuentaData.resumen?.total_notas_credito || 0).toLocaleString('es-CL')}
                </p>
              </Card>
              
              {/* Saldo Final */}
              <Card className={`p-3 border ${(estadoCuentaData.resumen?.saldo_final || 0) > 0 ? 'bg-primary/10 border-primary/30' : 'bg-green-100 border-green-300'}`}>
                <p className={`text-[10px] uppercase tracking-wide ${(estadoCuentaData.resumen?.saldo_final || 0) > 0 ? 'text-primary' : 'text-green-600'}`}>Saldo Final</p>
                <p className={`text-xl font-bold mt-1 ${(estadoCuentaData.resumen?.saldo_final || 0) > 0 ? 'text-primary' : 'text-green-700'}`}>
                  ${(estadoCuentaData.resumen?.saldo_final || 0).toLocaleString('es-CL')}
                </p>
              </Card>
            </div>
            
            {/* Tarjetas Vencido / Por Vencer */}
            <div className='grid grid-cols-2 gap-3'>
              <Card className='p-3 bg-red-50 border-red-200'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-[10px] text-red-600 uppercase tracking-wide'>Total Vencido</p>
                    <p className='text-lg font-bold text-red-700'>
                      ${(estadoCuentaData.resumen?.total_vencido || 0).toLocaleString('es-CL')}
                    </p>
                  </div>
                  {(estadoCuentaData.resumen?.total_vencido || 0) > 0 && (
                    <AlertTriangle className='w-6 h-6 text-red-500' />
                  )}
                </div>
              </Card>
              <Card className='p-3 bg-amber-50 border-amber-200'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-[10px] text-amber-600 uppercase tracking-wide'>Por Vencer</p>
                    <p className='text-lg font-bold text-amber-700'>
                      ${(estadoCuentaData.resumen?.total_por_vencer || 0).toLocaleString('es-CL')}
                    </p>
                  </div>
                  <Clock className='w-6 h-6 text-amber-500' />
                </div>
              </Card>
            </div>

            {/* Clasificación de Deuda (Aging) */}
            {estadoCuentaData.aging && (estadoCuentaData.resumen?.total_vencido || 0) > 0 && (
              <Card className='border-slate-200 overflow-hidden'>
                <div className='p-3 bg-slate-100 border-b border-slate-200'>
                  <h4 className='text-sm font-semibold text-slate-800 flex items-center gap-2'>
                    <TrendingDown className='w-4 h-4' />
                    Clasificación de Deuda (Aging)
                  </h4>
                </div>
                <div className='p-3'>
                  <div className='grid grid-cols-5 gap-2'>
                    {/* Al Día */}
                    <div className='text-center p-2 rounded-lg bg-green-50 border border-green-200'>
                      <p className='text-[10px] text-green-600 font-medium'>Al día</p>
                      <p className='text-sm font-bold text-green-700'>
                        ${(estadoCuentaData.aging.al_dia?.monto || 0).toLocaleString('es-CL')}
                      </p>
                      <p className='text-[10px] text-green-500'>
                        {estadoCuentaData.aging.al_dia?.cantidad || 0} doc.
                      </p>
                    </div>
                    {/* 1-30 días */}
                    <div className='text-center p-2 rounded-lg bg-yellow-50 border border-yellow-200'>
                      <p className='text-[10px] text-yellow-600 font-medium'>1-30 días</p>
                      <p className='text-sm font-bold text-yellow-700'>
                        ${(estadoCuentaData.aging['1_30_dias']?.monto || 0).toLocaleString('es-CL')}
                      </p>
                      <p className='text-[10px] text-yellow-500'>
                        {estadoCuentaData.aging['1_30_dias']?.cantidad || 0} doc.
                      </p>
                    </div>
                    {/* 31-60 días */}
                    <div className='text-center p-2 rounded-lg bg-orange-50 border border-orange-200'>
                      <p className='text-[10px] text-orange-600 font-medium'>31-60 días</p>
                      <p className='text-sm font-bold text-orange-700'>
                        ${(estadoCuentaData.aging['31_60_dias']?.monto || 0).toLocaleString('es-CL')}
                      </p>
                      <p className='text-[10px] text-orange-500'>
                        {estadoCuentaData.aging['31_60_dias']?.cantidad || 0} doc.
                      </p>
                    </div>
                    {/* 61-90 días */}
                    <div className='text-center p-2 rounded-lg bg-red-50 border border-red-200'>
                      <p className='text-[10px] text-red-600 font-medium'>61-90 días</p>
                      <p className='text-sm font-bold text-red-700'>
                        ${(estadoCuentaData.aging['61_90_dias']?.monto || 0).toLocaleString('es-CL')}
                      </p>
                      <p className='text-[10px] text-red-500'>
                        {estadoCuentaData.aging['61_90_dias']?.cantidad || 0} doc.
                      </p>
                    </div>
                    {/* +90 días */}
                    <div className='text-center p-2 rounded-lg bg-red-100 border border-red-300'>
                      <p className='text-[10px] text-red-700 font-medium'>+90 días</p>
                      <p className='text-sm font-bold text-red-800'>
                        ${(estadoCuentaData.aging.mas_90_dias?.monto || 0).toLocaleString('es-CL')}
                      </p>
                      <p className='text-[10px] text-red-600'>
                        {estadoCuentaData.aging.mas_90_dias?.cantidad || 0} doc.
                      </p>
                    </div>
                  </div>
                  {estadoCuentaData.aging.max_dias_atraso > 0 && (
                    <p className='text-xs text-slate-500 mt-2 text-center'>
                      Máximo atraso: <span className='font-semibold text-red-600'>{estadoCuentaData.aging.max_dias_atraso} días</span>
                    </p>
                  )}
                </div>
              </Card>
            )}

            {/* Tabla de Movimientos */}
            <Card className='border-slate-200 overflow-hidden'>
              <div className='p-3 bg-slate-100 border-b border-slate-200'>
                <h4 className='text-sm font-semibold text-slate-800 flex items-center gap-2'>
                  <Receipt className='w-4 h-4' />
                  Movimientos ({estadoCuentaData.movimientos?.length || 0})
                </h4>
              </div>
              
              {(estadoCuentaData.movimientos?.length || 0) === 0 ? (
                <div className='p-6 text-center text-slate-500 text-sm'>
                  No hay movimientos en el período seleccionado
                </div>
              ) : (
                <div className='overflow-x-auto max-h-[350px] overflow-y-auto'>
                  <table className='w-full text-xs'>
                    <thead className='bg-slate-50 sticky top-0 z-10'>
                      <tr>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>Fecha</th>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>Tipo</th>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>N° Doc.</th>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>Descripción</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Cargo</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Abono</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Fila de saldo inicial */}
                      <tr className='bg-slate-100 font-medium'>
                        <td className='p-2' colSpan={4}>Saldo Inicial</td>
                        <td className='p-2 text-right'>-</td>
                        <td className='p-2 text-right'>-</td>
                        <td className='p-2 text-right font-mono'>
                          ${(estadoCuentaData.resumen?.saldo_inicial || 0).toLocaleString('es-CL')}
                        </td>
                      </tr>
                      {estadoCuentaData.movimientos.map((mov, idx) => (
                        <tr key={idx} className='border-b border-slate-100 hover:bg-slate-50'>
                          <td className='p-2 text-slate-600'>
                            {mov.fecha ? new Date(mov.fecha).toLocaleDateString('es-CL') : 'N/A'}
                          </td>
                          <td className='p-2'>
                            <Badge 
                              variant='outline'
                              className={`text-[10px] px-1.5 py-0 flex items-center gap-1 w-fit ${
                                mov.tipo === 'invoice' ? 'bg-red-50 text-red-700 border-red-200' :
                                mov.tipo === 'payment' ? 'bg-green-50 text-green-700 border-green-200' :
                                mov.tipo === 'credit_note' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                'bg-orange-50 text-orange-700 border-orange-200'
                              }`}
                            >
                              {mov.tipo === 'invoice' && <Receipt className='w-3 h-3' />}
                              {mov.tipo === 'payment' && <DollarSign className='w-3 h-3' />}
                              {mov.tipo === 'credit_note' && <ArrowDownCircle className='w-3 h-3' />}
                              {mov.tipo === 'debit_note' && <ArrowUpCircle className='w-3 h-3' />}
                              {mov.tipo_display}
                            </Badge>
                          </td>
                          <td className='p-2 font-mono text-[10px]'>{mov.numero_documento}</td>
                          <td className='p-2 text-slate-600 max-w-[150px] truncate' title={mov.descripcion}>
                            {mov.descripcion}
                          </td>
                          <td className='p-2 text-right font-mono text-red-600'>
                            {mov.cargo_clp > 0 ? `$${mov.cargo_clp.toLocaleString('es-CL')}` : '-'}
                          </td>
                          <td className='p-2 text-right font-mono text-green-600'>
                            {mov.abono_clp > 0 ? `$${mov.abono_clp.toLocaleString('es-CL')}` : '-'}
                          </td>
                          <td className='p-2 text-right font-mono font-semibold'>
                            ${(mov.saldo_acumulado_clp || 0).toLocaleString('es-CL')}
                          </td>
                        </tr>
                      ))}
                      {/* Fila de saldo final */}
                      <tr className='bg-primary/10 font-bold'>
                        <td className='p-2' colSpan={4}>Saldo Final</td>
                        <td className='p-2 text-right text-red-700'>
                          ${(estadoCuentaData.resumen?.total_cargos || 0).toLocaleString('es-CL')}
                        </td>
                        <td className='p-2 text-right text-green-700'>
                          ${(estadoCuentaData.resumen?.total_abonos || 0).toLocaleString('es-CL')}
                        </td>
                        <td className='p-2 text-right font-mono text-primary text-base'>
                          ${(estadoCuentaData.resumen?.saldo_final || 0).toLocaleString('es-CL')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Documentos Pendientes */}
            {(estadoCuentaData.documentos_pendientes?.length || 0) > 0 && (
              <Card className='border-slate-200 overflow-hidden'>
                <div className='p-3 bg-amber-50 border-b border-amber-200'>
                  <h4 className='text-sm font-semibold text-amber-800 flex items-center gap-2'>
                    <AlertTriangle className='w-4 h-4' />
                    Documentos Pendientes ({estadoCuentaData.documentos_pendientes.length})
                  </h4>
                </div>
                <div className='overflow-x-auto max-h-[250px] overflow-y-auto'>
                  <table className='w-full text-xs'>
                    <thead className='bg-slate-50 sticky top-0'>
                      <tr>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>N° Factura</th>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>PSP</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Monto</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Pendiente</th>
                        <th className='text-center p-2 font-semibold text-slate-600 border-b'>Vencimiento</th>
                        <th className='text-center p-2 font-semibold text-slate-600 border-b'>Días Atraso</th>
                        <th className='text-center p-2 font-semibold text-slate-600 border-b'>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estadoCuentaData.documentos_pendientes.map((doc, idx) => (
                        <tr key={idx} className={`border-b border-slate-100 ${
                          doc.dias_atraso > 60 ? 'bg-red-100' :
                          doc.dias_atraso > 30 ? 'bg-orange-50' :
                          doc.es_vencida ? 'bg-yellow-50' : ''
                        }`}>
                          <td className='p-2 font-medium'>#{doc.numero_documento}</td>
                          <td className='p-2 text-[10px] text-slate-600'>{doc.psp_folio}</td>
                          <td className='p-2 text-right font-mono'>
                            ${(doc.monto_original || 0).toLocaleString('es-CL')}
                          </td>
                          <td className='p-2 text-right font-mono font-semibold text-red-700'>
                            ${(doc.saldo_pendiente || 0).toLocaleString('es-CL')}
                          </td>
                          <td className='p-2 text-center'>
                            {doc.fecha_vencimiento ? new Date(doc.fecha_vencimiento).toLocaleDateString('es-CL') : 'N/A'}
                          </td>
                          <td className='p-2 text-center'>
                            {doc.dias_atraso > 0 ? (
                              <span className={`font-bold ${
                                doc.dias_atraso > 60 ? 'text-red-700' :
                                doc.dias_atraso > 30 ? 'text-orange-600' :
                                'text-yellow-600'
                              }`}>
                                {doc.dias_atraso}
                              </span>
                            ) : (
                              <span className='text-green-600'>0</span>
                            )}
                          </td>
                          <td className='p-2 text-center'>
                            {doc.dias_atraso > 60 ? (
                              <Badge className='bg-red-200 text-red-800 border-red-300 text-[10px] px-1.5 py-0'>
                                Crítico
                              </Badge>
                            ) : doc.dias_atraso > 30 ? (
                              <Badge className='bg-orange-200 text-orange-800 border-orange-300 text-[10px] px-1.5 py-0'>
                                Alto
                              </Badge>
                            ) : doc.es_vencida ? (
                              <Badge className='bg-yellow-200 text-yellow-800 border-yellow-300 text-[10px] px-1.5 py-0'>
                                Vencida
                              </Badge>
                            ) : (
                              <Badge className='bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0'>
                                Vigente
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Cuotas por Facturar */}
            {(estadoCuentaData.cuotas_por_facturar?.length || 0) > 0 && (
              <Card className='border-slate-200 overflow-hidden' data-testid='card-cuotas-por-facturar'>
                <div className='p-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between'>
                  <h4 className='text-sm font-semibold text-blue-800 flex items-center gap-2'>
                    <FileText className='w-4 h-4' />
                    Cuotas por Facturar ({estadoCuentaData.cuotas_por_facturar.length})
                  </h4>
                  <div className='text-xs text-blue-700 font-medium'>
                    Total: <span className='font-bold'>${(estadoCuentaData.totales_cuotas_por_facturar?.total_clp || 0).toLocaleString('es-CL')}</span>
                    <span className='ml-2 text-blue-600'>({(estadoCuentaData.totales_cuotas_por_facturar?.total_uf || 0).toFixed(2)} UF)</span>
                  </div>
                </div>
                <div className='overflow-x-auto max-h-[250px] overflow-y-auto'>
                  <table className='w-full text-xs'>
                    <thead className='bg-slate-50 sticky top-0'>
                      <tr>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>PSP</th>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>Cuota</th>
                        <th className='text-left p-2 font-semibold text-slate-600 border-b'>Descripción</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Monto (UF)</th>
                        <th className='text-right p-2 font-semibold text-slate-600 border-b'>Monto (CLP)</th>
                        <th className='text-center p-2 font-semibold text-slate-600 border-b'>Vencimiento</th>
                        <th className='text-center p-2 font-semibold text-slate-600 border-b'>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estadoCuentaData.cuotas_por_facturar.map((c, idx) => (
                        <tr key={`cxf-${idx}`} className={`border-b border-slate-100 ${
                          c.es_vencida ? 'bg-amber-50' : ''
                        }`}>
                          <td className='p-2 font-medium text-slate-700'>{c.psp_folio}</td>
                          <td className='p-2 text-center'>#{c.cuota_numero}</td>
                          <td className='p-2 text-[10px] text-slate-600'>
                            {c.descripcion}
                            {c.es_cuota_exito && (
                              <Badge className='ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px] px-1 py-0'>
                                Fee Resultado
                              </Badge>
                            )}
                          </td>
                          <td className='p-2 text-right font-mono'>{(c.monto_uf || 0).toFixed(2)}</td>
                          <td className='p-2 text-right font-mono'>${(c.monto_clp || 0).toLocaleString('es-CL')}</td>
                          <td className='p-2 text-center'>
                            {c.fecha_vencimiento ? new Date(c.fecha_vencimiento).toLocaleDateString('es-CL') : '—'}
                          </td>
                          <td className='p-2 text-center'>
                            {c.billing_diferida ? (
                              <Badge className='bg-orange-100 text-orange-700 border-orange-200 text-[10px] px-1.5 py-0'>
                                Diferida
                              </Badge>
                            ) : c.es_vencida ? (
                              <Badge className='bg-amber-200 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0'>
                                Por facturar (vencida)
                              </Badge>
                            ) : (
                              <Badge className='bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0'>
                                Por facturar
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Nota al pie */}
            <div className='text-[10px] text-slate-500 text-center'>
              Este estado de cuenta es informativo. Para consultas contacte a {estadoCuentaData.cliente?.email || 'finanzas@empresa.cl'}
            </div>
          </div>
        )}
      </div>
    </TabsContent>

    {/* Pestaña: Gestiones de Cobranza */}
    <TabsContent value='gestiones' className='mt-0'>
      <div className='space-y-4'>
        {/* Header con botones de acción */}
        <div className='flex items-center justify-between'>
          <div>
            <h4 className='text-lg font-semibold text-slate-800 flex items-center gap-2'>
              <PhoneCall className='w-5 h-5 text-amber-600' />
              Gestión de Acciones de Cobranza
            </h4>
            <p className='text-sm text-slate-500'>
              Registre y haga seguimiento a las acciones de cobranza y compromisos de pago
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <Button 
              size='sm' 
              variant='outline'
              onClick={() => {
                fetchGestionesCobranza();
                fetchCompromisosCobranza();
              }}
              className='text-xs'
            >
              <RefreshCw className='w-3 h-3 mr-1' />
              Actualizar
            </Button>
            <Button 
              size='sm' 
              onClick={() => setShowNuevoCompromiso(true)}
              className='bg-green-600 hover:bg-green-700 text-white text-xs'
              data-testid='btn-nuevo-compromiso'
            >
              <CreditCard className='w-3 h-3 mr-1' />
              Nuevo Compromiso
            </Button>
            <Button 
              size='sm' 
              onClick={() => setShowNuevaGestion(true)}
              className='bg-amber-600 hover:bg-amber-700 text-white text-xs'
              data-testid='btn-nueva-gestion'
            >
              <Plus className='w-3 h-3 mr-1' />
              Nueva Gestión
            </Button>
          </div>
        </div>

        {/* Resumen de compromisos y alertas */}
        <div className='grid grid-cols-4 gap-4'>
          <Card className='p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg bg-amber-100'>
                <PhoneCall className='w-4 h-4 text-amber-600' />
              </div>
              <div>
                <p className='text-xs text-slate-500'>Gestiones del mes</p>
                <p className='text-xl font-bold text-amber-700'>{gestionesCobranza?.length || 0}</p>
              </div>
            </div>
          </Card>
          <Card className='p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg bg-green-100'>
                <CreditCard className='w-4 h-4 text-green-600' />
              </div>
              <div>
                <p className='text-xs text-slate-500'>Compromisos pendientes</p>
                <p className='text-xl font-bold text-green-700'>
                  {compromisosCobranza?.filter(c => c.estado === 'pendiente').length || 0}
                </p>
              </div>
            </div>
          </Card>
          <Card className='p-3 bg-gradient-to-r from-red-50 to-rose-50 border-red-200'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg bg-red-100'>
                <AlertTriangle className='w-4 h-4 text-red-600' />
              </div>
              <div>
                <p className='text-xs text-slate-500'>Compromisos vencidos</p>
                <p className='text-xl font-bold text-red-700'>
                  {compromisosCobranza?.filter(c => c.estado === 'incumplido').length || 0}
                </p>
              </div>
            </div>
          </Card>
          <Card className='p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg bg-blue-100'>
                <CheckCircle className='w-4 h-4 text-blue-600' />
              </div>
              <div>
                <p className='text-xs text-slate-500'>Compromisos cumplidos</p>
                <p className='text-xl font-bold text-blue-700'>
                  {compromisosCobranza?.filter(c => c.estado === 'cumplido').length || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filtros de gestiones */}
        <Card className='p-3 bg-slate-50 border-slate-200'>
          <div className='flex items-center gap-4 flex-wrap'>
            <div className='flex-1 min-w-[200px]'>
              <Label className='text-xs text-slate-500 mb-1 block'>Cliente</Label>
              <Select value={filtroGestionCliente || 'todos'} onValueChange={(val) => setFiltroGestionCliente(val === 'todos' ? '' : val)}>
                <SelectTrigger className='h-9 text-sm'>
                  <SelectValue placeholder='Todos los clientes' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='todos'>Todos los clientes</SelectItem>
                  {clientesConFacturas.map(cliente => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.name} {cliente.rut && `(${formatRutSinPuntos(cliente.rut)})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='w-44'>
              <Label className='text-xs text-slate-500 mb-1 block'>Tipo de gestión</Label>
              <Select value={filtroGestionTipo} onValueChange={setFiltroGestionTipo}>
                <SelectTrigger className='h-9 text-sm'>
                  <SelectValue placeholder='Todos' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='todos'>Todos</SelectItem>
                  {tiposGestion.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='w-44'>
              <Label className='text-xs text-slate-500 mb-1 block'>Resultado</Label>
              <Select value={filtroGestionResultado} onValueChange={setFiltroGestionResultado}>
                <SelectTrigger className='h-9 text-sm'>
                  <SelectValue placeholder='Todos' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='todos'>Todos</SelectItem>
                  {resultadosGestion.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='pt-5'>
              <Button 
                size='sm' 
                variant='outline'
                onClick={fetchGestionesCobranza}
                className='text-xs'
              >
                <Search className='w-3 h-3 mr-1' />
                Filtrar
              </Button>
            </div>
          </div>
        </Card>

        {/* Panel de compromisos pendientes/vencidos */}
        {(compromisosCobranza?.filter(c => c.estado === 'pendiente' || c.estado === 'incumplido').length > 0) && (
          <Card className='p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-300'>
            <h5 className='text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2'>
              <AlertTriangle className='w-4 h-4' />
              Compromisos que requieren atención
            </h5>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {compromisosCobranza?.filter(c => c.estado === 'pendiente' || c.estado === 'incumplido').slice(0, 6).map(comp => (
                <div key={comp.id} className={`p-3 rounded-lg border ${comp.estado === 'incumplido' ? 'bg-red-50 border-red-200' : 'bg-white border-amber-200'}`}>
                  <div className='flex justify-between items-start mb-2'>
                    <div>
                      <p className='text-sm font-medium text-slate-800'>{comp.cliente_nombre}</p>
                      <p className='text-xs text-slate-500'>Fecha: {comp.fecha_compromiso}</p>
                    </div>
                    <Badge className={comp.estado === 'incumplido' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}>
                      {comp.estado === 'incumplido' ? 'Vencido' : 'Pendiente'}
                    </Badge>
                  </div>
                  <p className='text-lg font-bold text-slate-900 mb-2'>
                    ${new Intl.NumberFormat('es-CL').format(comp.monto_comprometido)}
                  </p>
                  {comp.observaciones && <p className='text-xs text-slate-500 mb-2'>{comp.observaciones}</p>}
                  <div className='flex gap-2'>
                    <Button 
                      size='sm' 
                      variant='outline' 
                      className='text-xs flex-1 border-green-300 text-green-700 hover:bg-green-50'
                      onClick={() => handleActualizarCompromiso(comp.id, 'cumplido', comp.monto_comprometido, 'Pago recibido')}
                    >
                      <CheckCircle className='w-3 h-3 mr-1' />
                      Cumplido
                    </Button>
                    <Button 
                      size='sm' 
                      variant='outline' 
                      className='text-xs flex-1 border-blue-300 text-blue-700 hover:bg-blue-50'
                      onClick={() => {
                        setClienteGestionSeleccionado({ id: comp.cliente_id, name: comp.cliente_nombre });
                        setNuevaGestion(prev => ({
                          ...prev,
                          cliente_id: comp.cliente_id,
                          cliente_nombre: comp.cliente_nombre,
                          tipo_gestion: 'seguimiento_interno',
                          resumen: `Seguimiento a compromiso del ${comp.fecha_compromiso} por $${new Intl.NumberFormat('es-CL').format(comp.monto_comprometido)}`
                        }));
                        setShowNuevaGestion(true);
                      }}
                    >
                      <PhoneCall className='w-3 h-3 mr-1' />
                      Registrar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Tabla de gestiones recientes */}
        <Card className='border-slate-200'>
          <div className='p-4 border-b border-slate-100'>
            <h5 className='text-sm font-semibold text-slate-800'>Historial de Gestiones</h5>
          </div>
          {loadingGestiones ? (
            <div className='p-8 text-center'>
              <RefreshCw className='w-6 h-6 animate-spin text-amber-500 mx-auto mb-2' />
              <p className='text-sm text-slate-500'>Cargando gestiones...</p>
            </div>
          ) : gestionesCobranza?.length === 0 ? (
            <div className='p-8 text-center'>
              <PhoneCall className='w-10 h-10 text-slate-300 mx-auto mb-2' />
              <p className='text-sm text-slate-500'>No hay gestiones registradas</p>
              <p className='text-xs text-slate-400 mt-1'>Use el botón "Nueva Gestión" para registrar una acción de cobranza</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-slate-50'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Fecha</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Cliente</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Tipo</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Canal</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Resumen</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Resultado</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Usuario</th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-slate-600'>Acciones</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {gestionesCobranza?.map(gestion => {
                    const tipoInfo = tiposGestion.find(t => t.value === gestion.tipo_gestion);
                    const resultadoInfo = resultadosGestion.find(r => r.value === gestion.resultado);
                    const TipoIcon = tipoInfo?.icon || FileText;
                    
                    return (
                      <tr key={gestion.id} className='hover:bg-slate-50' data-testid={`gestion-row-${gestion.id}`}>
                        <td className='px-4 py-3'>
                          <div className='text-xs'>
                            <p className='font-medium text-slate-700'>
                              {new Date(gestion.fecha).toLocaleDateString('es-CL')}
                            </p>
                            <p className='text-slate-400'>
                              {new Date(gestion.fecha).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </td>
                        <td className='px-4 py-3'>
                          <p className='text-sm font-medium text-slate-800'>{gestion.cliente_nombre || 'Sin cliente'}</p>
                        </td>
                        <td className='px-4 py-3'>
                          <div className='flex items-center gap-1.5'>
                            <TipoIcon className='w-3.5 h-3.5 text-amber-600' />
                            <span className='text-xs text-slate-600'>{tipoInfo?.label || gestion.tipo_gestion}</span>
                          </div>
                        </td>
                        <td className='px-4 py-3'>
                          <Badge variant='outline' className='text-[10px] px-1.5 py-0'>
                            {canalesGestion.find(c => c.value === gestion.canal)?.label || gestion.canal}
                          </Badge>
                        </td>
                        <td className='px-4 py-3 max-w-xs'>
                          <p className='text-xs text-slate-600 truncate'>{gestion.resumen}</p>
                          {gestion.proxima_accion && (
                            <p className='text-[10px] text-blue-600 mt-0.5'>
                              Próxima: {gestion.proxima_accion}
                            </p>
                          )}
                        </td>
                        <td className='px-4 py-3'>
                          {gestion.resultado ? (
                            <Badge className={`text-[10px] px-1.5 py-0 bg-${resultadoInfo?.color || 'slate'}-100 text-${resultadoInfo?.color || 'slate'}-700 border-${resultadoInfo?.color || 'slate'}-200`}>
                              {resultadoInfo?.label || gestion.resultado}
                            </Badge>
                          ) : (
                            <span className='text-xs text-slate-400'>-</span>
                          )}
                        </td>
                        <td className='px-4 py-3'>
                          <span className='text-xs text-slate-500'>{gestion.created_by_name}</span>
                        </td>
                        <td className='px-4 py-3'>
                          <Button 
                            size='sm' 
                            variant='ghost' 
                            className='h-7 px-2'
                            onClick={() => handleVerHistorialGestiones(gestion.cliente_id)}
                            data-testid={`ver-historial-${gestion.id}`}
                          >
                            <History className='w-3.5 h-3.5 text-slate-500' />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </TabsContent>

    {/* Pestaña: Alertas de Cobranza (Etapa 7) */}
    <TabsContent value='alertas' className='mt-0'>
      <div className='space-y-4'>
        {/* Header con botones de acción */}
        <div className='flex items-center justify-between'>
          <div>
            <h4 className='text-lg font-semibold text-slate-800 flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5 text-red-600' />
              Panel de Alertas de Cobranza
            </h4>
            <p className='text-sm text-slate-500'>
              Sistema proactivo de alertas basado en vencimientos, compromisos y políticas de cobranza
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <Button 
              size='sm' 
              variant='outline'
              onClick={() => {
                fetchHistorialAlertas();
                setShowHistorialAlertas(true);
              }}
              className='text-xs'
            >
              <History className='w-3 h-3 mr-1' />
              Ver Historial
            </Button>
            <Button 
              size='sm' 
              variant='outline'
              onClick={fetchAlertasCobranza}
              className='text-xs'
            >
              <RefreshCw className='w-3 h-3 mr-1' />
              Actualizar
            </Button>
            <Button 
              size='sm' 
              onClick={handleGenerarAlertas}
              disabled={generandoAlertas}
              className='bg-red-600 hover:bg-red-700 text-white text-xs'
              data-testid='btn-generar-alertas'
            >
              {generandoAlertas ? (
                <RefreshCw className='w-3 h-3 mr-1 animate-spin' />
              ) : (
                <Zap className='w-3 h-3 mr-1' />
              )}
              Generar Alertas
            </Button>
          </div>
        </div>

        {loadingAlertas ? (
          <div className='p-8 text-center'>
            <RefreshCw className='w-6 h-6 animate-spin text-red-500 mx-auto mb-2' />
            <p className='text-sm text-slate-500'>Cargando alertas...</p>
          </div>
        ) : !alertasCobranza ? (
          <div className='p-8 text-center'>
            <AlertTriangle className='w-10 h-10 text-slate-300 mx-auto mb-2' />
            <p className='text-sm text-slate-500'>No se pudieron cargar las alertas</p>
            <Button 
              size='sm' 
              variant='outline' 
              onClick={handleGenerarAlertas}
              className='mt-2'
            >
              Generar alertas ahora
            </Button>
          </div>
        ) : (
          <>
            {/* Resumen de estadísticas */}
            <div className='grid grid-cols-3 gap-4'>
              <Card className='p-4 bg-gradient-to-r from-slate-50 to-blue-50 border-blue-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-blue-100'>
                    <AlertTriangle className='w-5 h-5 text-blue-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Total alertas activas</p>
                    <p className='text-2xl font-bold text-blue-700'>
                      {alertasCobranza.estadisticas?.total_alertas || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className='p-4 bg-gradient-to-r from-red-50 to-rose-50 border-red-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-red-100'>
                    <XCircle className='w-5 h-5 text-red-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Alertas críticas</p>
                    <p className='text-2xl font-bold text-red-700'>
                      {alertasCobranza.estadisticas?.criticas || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className='p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-amber-100'>
                    <DollarSign className='w-5 h-5 text-amber-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Monto en riesgo</p>
                    <p className='text-2xl font-bold text-amber-700'>
                      ${new Intl.NumberFormat('es-CL').format(alertasCobranza.estadisticas?.monto_en_riesgo || 0)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Secciones de alertas */}
            <div className='grid grid-cols-2 gap-4'>
              {Object.entries(alertasCobranza.secciones || {}).map(([key, seccion]) => {
                const colorMap = {
                  amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', badge: 'bg-amber-100' },
                  orange: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-100' },
                  red: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', badge: 'bg-red-100' },
                  blue: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', badge: 'bg-blue-100' }
                };
                const colors = colorMap[seccion.color] || colorMap.amber;
                const IconComponent = iconosAlerta[seccion.alertas?.[0]?.tipo_alerta] || AlertTriangle;

                return (
                  <Card 
                    key={key} 
                    className={`p-4 ${colors.bg} ${colors.border} ${seccion.cantidad === 0 ? 'opacity-50' : ''}`}
                  >
                    <div className='flex items-center justify-between mb-3'>
                      <h5 className={`text-sm font-semibold ${colors.text} flex items-center gap-2`}>
                        <IconComponent className='w-4 h-4' />
                        {seccion.titulo}
                      </h5>
                      <Badge className={`${colors.badge} ${colors.text} border-none`}>
                        {seccion.cantidad}
                      </Badge>
                    </div>

                    {seccion.cantidad === 0 ? (
                      <p className='text-xs text-slate-400 text-center py-2'>
                        Sin alertas en esta categoría
                      </p>
                    ) : (
                      <div className='space-y-2 max-h-[200px] overflow-y-auto'>
                        {seccion.alertas?.slice(0, 5).map((alerta, idx) => {
                          const prioridadColor = coloresPrioridad[alerta.prioridad] || coloresPrioridad.media;
                          const AlertIcon = iconosAlerta[alerta.tipo_alerta] || AlertTriangle;
                          
                          return (
                            <div 
                              key={alerta.id || idx} 
                              className={`p-2 rounded-lg bg-white border ${prioridadColor.border} hover:shadow-sm transition-shadow`}
                            >
                              <div className='flex justify-between items-start mb-1'>
                                <div className='flex items-center gap-1.5'>
                                  <AlertIcon className={`w-3.5 h-3.5 ${prioridadColor.text}`} />
                                  <span className='text-xs font-medium text-slate-800'>
                                    {alerta.cliente_nombre}
                                  </span>
                                </div>
                                <Badge className={`text-[9px] px-1 py-0 ${prioridadColor.bg} ${prioridadColor.text}`}>
                                  {alerta.prioridad}
                                </Badge>
                              </div>
                              <p className='text-[11px] text-slate-600 mb-2 line-clamp-2'>
                                {alerta.mensaje}
                              </p>
                              {alerta.sugerencia_accion && (
                                <div className='flex items-center gap-1 text-[10px] text-blue-600 mb-2'>
                                  <Lightbulb className='w-3 h-3' />
                                  <span>{sugerenciasAccion[alerta.sugerencia_accion]?.label || alerta.sugerencia_accion}</span>
                                </div>
                              )}
                              <div className='flex gap-1'>
                                <Button 
                                  size='sm' 
                                  variant='outline' 
                                  className='h-6 text-[10px] px-2 flex-1'
                                  onClick={() => handleActualizarAlerta(alerta.id, 'revisada')}
                                >
                                  <Eye className='w-3 h-3 mr-1' />
                                  Revisada
                                </Button>
                                <Button 
                                  size='sm' 
                                  className='h-6 text-[10px] px-2 flex-1 bg-amber-600 hover:bg-amber-700'
                                  onClick={() => {
                                    setAlertaSeleccionada(alerta);
                                    setShowCrearGestionDesdeAlerta(true);
                                  }}
                                >
                                  <PhoneCall className='w-3 h-3 mr-1' />
                                  Gestionar
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                        {seccion.cantidad > 5 && (
                          <p className='text-xs text-center text-slate-400 pt-1'>
                            +{seccion.cantidad - 5} más...
                          </p>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Leyenda de sugerencias de acción */}
            <Card className='p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'>
              <h5 className='text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2'>
                <Lightbulb className='w-4 h-4' />
                Guía de Acciones Sugeridas según Antigüedad de Deuda
              </h5>
              <div className='grid grid-cols-5 gap-3'>
                <div className='text-center p-2 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-slate-700'>0-5 días</p>
                  <p className='text-[10px] text-slate-500'>Recordatorio cordial</p>
                </div>
                <div className='text-center p-2 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-amber-700'>7-15 días</p>
                  <p className='text-[10px] text-slate-500'>Contacto telefónico</p>
                </div>
                <div className='text-center p-2 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-orange-700'>30 días</p>
                  <p className='text-[10px] text-slate-500'>Seguimiento formal</p>
                </div>
                <div className='text-center p-2 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-red-700'>60 días</p>
                  <p className='text-[10px] text-slate-500'>Revisión interna</p>
                </div>
                <div className='text-center p-2 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-red-900'>90+ días</p>
                  <p className='text-[10px] text-slate-500'>Derivación judicial</p>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </TabsContent>

    {/* Pestaña: Comunicaciones de Cobranza (Etapa 8) */}
    <TabsContent value='comunicaciones' className='mt-0'>
      <div className='space-y-4'>
        {/* Header con botones de acción */}
        <div className='flex items-center justify-between'>
          <div>
            <h4 className='text-lg font-semibold text-slate-800 flex items-center gap-2'>
              <Mail className='w-5 h-5 text-blue-600' />
              Comunicaciones de Cobranza
            </h4>
            <p className='text-sm text-slate-500'>
              Sistema automatizado de envío de estados de cuenta y recordatorios de pago
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <Button 
              size='sm' 
              variant='outline'
              onClick={() => {
                fetchConfigComunicaciones();
                setShowConfigComunicaciones(true);
              }}
              className='text-xs'
            >
              <Settings className='w-3 h-3 mr-1' />
              Configuración
            </Button>
            <Button 
              size='sm' 
              variant='outline'
              onClick={fetchComunicacionesPanel}
              className='text-xs'
            >
              <RefreshCw className='w-3 h-3 mr-1' />
              Actualizar
            </Button>
            <Button 
              size='sm' 
              onClick={handleEjecutarAutomatico}
              disabled={ejecutandoAutomatico}
              className='bg-blue-600 hover:bg-blue-700 text-white text-xs'
              data-testid='btn-ejecutar-automatico'
            >
              {ejecutandoAutomatico ? (
                <RefreshCw className='w-3 h-3 mr-1 animate-spin' />
              ) : (
                <Zap className='w-3 h-3 mr-1' />
              )}
              Ejecutar Automático
            </Button>
          </div>
        </div>

        {loadingComunicaciones ? (
          <div className='p-8 text-center'>
            <RefreshCw className='w-6 h-6 animate-spin text-blue-500 mx-auto mb-2' />
            <p className='text-sm text-slate-500'>Cargando comunicaciones...</p>
          </div>
        ) : !comunicacionesPanel ? (
          <div className='p-8 text-center'>
            <Mail className='w-10 h-10 text-slate-300 mx-auto mb-2' />
            <p className='text-sm text-slate-500'>No se pudieron cargar las comunicaciones</p>
          </div>
        ) : (
          <>
            {/* Alerta de Resend no configurado */}
            {!comunicacionesPanel.configuracion?.email_configurado && (
              <Card className='p-4 bg-amber-50 border-amber-300'>
                <div className='flex items-start gap-3'>
                  <AlertTriangle className='w-5 h-5 text-amber-600 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-amber-800'>Modo Simulado Activo</p>
                    <p className='text-xs text-amber-700 mt-1'>
                      El servicio de email (Resend) no está configurado. Las comunicaciones se registran pero no se envían realmente.
                      Configure Resend para activar el envío real de correos.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Resumen de estadísticas */}
            <div className='grid grid-cols-4 gap-4'>
              <Card className='p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-blue-100'>
                    <Mail className='w-5 h-5 text-blue-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Enviados hoy</p>
                    <p className='text-2xl font-bold text-blue-700'>
                      {comunicacionesPanel.estadisticas_hoy?.enviados || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className='p-4 bg-gradient-to-r from-red-50 to-rose-50 border-red-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-red-100'>
                    <XCircle className='w-5 h-5 text-red-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Fallidos</p>
                    <p className='text-2xl font-bold text-red-700'>
                      {comunicacionesPanel.estadisticas_hoy?.fallidos || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className='p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-amber-100'>
                    <UserX className='w-5 h-5 text-amber-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Clientes sin email</p>
                    <p className='text-2xl font-bold text-amber-700'>
                      {comunicacionesPanel.clientes_sin_email?.length || 0}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className='p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-lg bg-green-100'>
                    <CheckCircle className='w-5 h-5 text-green-600' />
                  </div>
                  <div>
                    <p className='text-xs text-slate-500'>Estado</p>
                    <p className='text-lg font-bold text-green-700'>
                      {comunicacionesPanel.configuracion?.comunicaciones_activas ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Grid de dos columnas */}
            <div className='grid grid-cols-2 gap-4'>
              {/* Comunicaciones de hoy */}
              <Card className='border-slate-200'>
                <div className='p-4 border-b border-slate-100 flex justify-between items-center'>
                  <h5 className='text-sm font-semibold text-slate-800 flex items-center gap-2'>
                    <Clock className='w-4 h-4 text-blue-600' />
                    Comunicaciones de Hoy
                  </h5>
                  <Badge variant='outline' className='text-[10px]'>
                    {comunicacionesPanel.comunicaciones_hoy?.length || 0}
                  </Badge>
                </div>
                <div className='p-4 max-h-[300px] overflow-y-auto'>
                  {comunicacionesPanel.comunicaciones_hoy?.length === 0 ? (
                    <p className='text-xs text-slate-400 text-center py-4'>
                      No hay comunicaciones enviadas hoy
                    </p>
                  ) : (
                    <div className='space-y-2'>
                      {comunicacionesPanel.comunicaciones_hoy?.map((com, idx) => {
                        const tipoInfo = tiposComunicacion[com.tipo] || tiposComunicacion.notificacion_manual;
                        const estadoInfo = estadosComunicacion[com.estado] || estadosComunicacion.pendiente;
                        const TipoIcon = tipoInfo.icon || Mail;
                        
                        return (
                          <div key={com.id || idx} className='p-2 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors'>
                            <div className='flex justify-between items-start mb-1'>
                              <div className='flex items-center gap-1.5'>
                                <TipoIcon className={`w-3.5 h-3.5 text-${tipoInfo.color}-600`} />
                                <span className='text-xs font-medium text-slate-800'>
                                  {com.cliente_nombre}
                                </span>
                              </div>
                              <Badge className={`text-[9px] px-1 py-0 bg-${estadoInfo.color}-100 text-${estadoInfo.color}-700`}>
                                {estadoInfo.label}
                              </Badge>
                            </div>
                            <p className='text-[11px] text-slate-600 truncate'>{com.asunto}</p>
                            <p className='text-[10px] text-slate-400 mt-1'>
                              {new Date(com.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })} • {com.email_destinatario}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>

              {/* Clientes sin email */}
              <Card className='border-slate-200'>
                <div className='p-4 border-b border-slate-100 flex justify-between items-center'>
                  <h5 className='text-sm font-semibold text-slate-800 flex items-center gap-2'>
                    <UserX className='w-4 h-4 text-amber-600' />
                    Clientes sin Email Registrado
                  </h5>
                  <Badge variant='outline' className='text-[10px] border-amber-300 text-amber-700'>
                    {comunicacionesPanel.clientes_sin_email?.length || 0}
                  </Badge>
                </div>
                <div className='p-4 max-h-[300px] overflow-y-auto'>
                  {comunicacionesPanel.clientes_sin_email?.length === 0 ? (
                    <p className='text-xs text-green-600 text-center py-4 flex items-center justify-center gap-2'>
                      <CheckCircle className='w-4 h-4' />
                      Todos los clientes tienen email registrado
                    </p>
                  ) : (
                    <div className='space-y-2'>
                      {comunicacionesPanel.clientes_sin_email?.map((cliente, idx) => (
                        <div key={cliente.id || idx} className='p-2 rounded-lg border border-amber-200 bg-amber-50'>
                          <div className='flex justify-between items-center'>
                            <div>
                              <p className='text-xs font-medium text-slate-800'>{cliente.nombre}</p>
                              {cliente.rut && <p className='text-[10px] text-slate-500'>{formatRutSinPuntos(cliente.rut)}</p>}
                            </div>
                            <AlertTriangle className='w-4 h-4 text-amber-500' />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Acciones rápidas */}
            <Card className='p-4 bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200'>
              <h5 className='text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2'>
                <Zap className='w-4 h-4 text-blue-600' />
                Envío Manual de Comunicaciones
              </h5>
              <div className='grid grid-cols-3 gap-3'>
                <Button 
                  variant='outline' 
                  className='h-auto py-3 flex flex-col items-center gap-2 border-blue-300 hover:bg-blue-50'
                  onClick={() => {
                    setClienteEnvioSeleccionado(null);
                    setShowEnviarEstadoCuenta(true);
                  }}
                >
                  <FileText className='w-6 h-6 text-blue-600' />
                  <span className='text-xs font-medium'>Enviar Estado de Cuenta</span>
                  <span className='text-[10px] text-slate-500'>A un cliente específico</span>
                </Button>
                <Button 
                  variant='outline' 
                  className='h-auto py-3 flex flex-col items-center gap-2 border-amber-300 hover:bg-amber-50'
                  onClick={() => {
                    fetchPlantillasCorreo();
                    setShowEnviarRecordatorio(true);
                  }}
                >
                  <AlertTriangle className='w-6 h-6 text-amber-600' />
                  <span className='text-xs font-medium'>Enviar Recordatorio</span>
                  <span className='text-[10px] text-slate-500'>Por factura vencida</span>
                </Button>
                <Button 
                  variant='outline' 
                  className='h-auto py-3 flex flex-col items-center gap-2 border-green-300 hover:bg-green-50'
                  onClick={handleEjecutarAutomatico}
                  disabled={ejecutandoAutomatico}
                >
                  <Zap className='w-6 h-6 text-green-600' />
                  <span className='text-xs font-medium'>Ejecutar Todos</span>
                  <span className='text-[10px] text-slate-500'>Según reglas configuradas</span>
                </Button>
              </div>
            </Card>

            {/* Información de reglas */}
            <Card className='p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'>
              <h5 className='text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2'>
                <Info className='w-4 h-4' />
                Reglas de Comunicación Automática
              </h5>
              <div className='grid grid-cols-3 gap-4'>
                <div className='text-center p-3 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-slate-700'>Estados de Cuenta</p>
                  <p className='text-[10px] text-slate-500 mt-1'>Día 1 de cada mes</p>
                  <Badge className='mt-2 text-[10px] bg-blue-100 text-blue-700'>Mensual</Badge>
                </div>
                <div className='text-center p-3 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-slate-700'>Recordatorios Vencidas</p>
                  <p className='text-[10px] text-slate-500 mt-1'>Días 0, 7, 15, 30</p>
                  <Badge className='mt-2 text-[10px] bg-red-100 text-red-700'>Automático</Badge>
                </div>
                <div className='text-center p-3 bg-white rounded-lg'>
                  <p className='text-xs font-medium text-slate-700'>Compromisos de Pago</p>
                  <p className='text-[10px] text-slate-500 mt-1'>1 día antes y el día del compromiso</p>
                  <Badge className='mt-2 text-[10px] bg-green-100 text-green-700'>Automático</Badge>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </TabsContent>
  </Tabs>
</div>

  );
};

export default CobranzaTab;
